// Per-part deep editor: edge bands + drilling overrides.
//
// Opens as a modal from CabinetEditor. Reads the cabinet class's
// computed parts + drillPattern() and lets the user:
//   • toggle the four edges (top/bottom/left/right) — pick a band id
//     from the project's edge_bands catalog, or "no banding"
//   • suppress individual standard drillings (32mm row, hinge cups)
//   • add custom drillings (cable pass-through, finger pulls, etc.)
//
// Edits write `Cabinet.partOverrides[partId]` and the changes flow
// through partsFor() (for BOM/cutlist/edge totals) and
// effectiveDrillings() (for MPR / WoodWop output).

import { useEffect, useMemo, useRef, useState } from "react";

import { getCabinetClass, type DrillEntry } from "@/lib/cabinets";
import { partsFor, autoDrillingsFor } from "@/lib/cabinets/bom";
import type { CatalogIndex } from "@/lib/cabinets/bom";
import { validateCabinet, type Issue } from "@/lib/cabinets/validation";
import type {
  Cabinet,
  CabinetPart,
  CustomDrilling,
  EdgeBand,
  Id,
  PartOverride,
} from "@/lib/types";
import { PartPreview } from "./PartPreview";

interface Props {
  cabinet: Cabinet;
  catalog: CatalogIndex;
  edgeBands: EdgeBand[];
  onClose: () => void;
  onChange: (next: Cabinet["partOverrides"]) => void;
  /** Open the modal with this part pre-selected in the left rail.
   *  Defaults to the first part. Used by the outliner so clicking a
   *  part row jumps directly to its form. */
  initialPartId?: string | null;
}

export function PartEditor({ cabinet, catalog, edgeBands, onClose, onChange, initialPartId }: Props) {
  const cls = getCabinetClass(cabinet.classId);
  const parts = useMemo(() => partsFor(cabinet, catalog), [cabinet, catalog]);
  const dimsKey = `${cabinet.width}_${cabinet.height}_${cabinet.depth}`;
  const standardPattern = useMemo(
    () => cls?.drillPattern({ w: cabinet.width, h: cabinet.height, d: cabinet.depth }) ?? {},
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [cls, dimsKey],
  );
  // Per-cabinet drillings the class evaluator can't see — drawer slide
  // screws + leg-mount screws. Resolved per-part below via autoDrillingsFor().
  const allIssues = useMemo(() => validateCabinet(cabinet, catalog), [cabinet, catalog]);
  const issuesByPart = useMemo(() => {
    const m = new Map<string, Issue[]>();
    for (const iss of allIssues) {
      if (!iss.partId) continue;
      const arr = m.get(iss.partId) ?? [];
      arr.push(iss);
      m.set(iss.partId, arr);
    }
    return m;
  }, [allIssues]);

  const [selectedPart, setSelectedPart] = useState<string | null>(
    () => initialPartId ?? parts[0]?.id ?? null,
  );
  // If the parent steers the selection (e.g. outliner click) after the
  // modal is already open, follow along.
  useEffect(() => {
    if (initialPartId) setSelectedPart(initialPartId);
  }, [initialPartId]);
  const overrides = cabinet.partOverrides ?? {};

  const updatePart = (partId: string, next: PartOverride | null) => {
    const nextAll: Record<string, PartOverride> = { ...overrides };
    if (next === null || (
      !next.edges
      && !next.extraDrillings?.length
      && !next.suppressDrillings?.length
      && next.materialId == null
      && next.enabled === undefined
      && next.thickness === undefined
      && (!next.params || Object.keys(next.params).length === 0)
    )) {
      delete nextAll[partId];
    } else {
      nextAll[partId] = next;
    }
    onChange(nextAll);
  };

  return (
    <div
      onClick={onClose}
      style={{
        position: "fixed", inset: 0, zIndex: 1000,
        background: "rgba(0,0,0,0.65)",
        display: "grid", placeItems: "center",
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="card"
        style={{
          width: 920, maxWidth: "94vw", maxHeight: "88vh",
          display: "grid", gridTemplateColumns: "220px 1fr",
          gridTemplateRows: "auto 1fr", gap: 0, overflow: "hidden",
        }}
      >
        <header style={{
          gridColumn: "1 / -1",
          padding: "12px 16px",
          borderBottom: "1px solid var(--line)",
          display: "flex", alignItems: "center", justifyContent: "space-between",
        }}>
          <div>
            <h3 style={{ margin: 0, fontSize: 14 }}>Parts of cabinet #{cabinet._id}</h3>
            <div className="muted" style={{ fontSize: 11, marginTop: 2 }}>
              {cls?.label ?? cabinet.classId} · {cabinet.width}″ × {cabinet.height}″ × {cabinet.depth}″
            </div>
          </div>
          <button className="ghost" onClick={onClose}>Done</button>
        </header>

        <aside style={{
          borderRight: "1px solid var(--line)",
          overflowY: "auto",
          padding: 8,
        }}>
          {(() => {
            const errors  = allIssues.filter((i) => i.severity === "error").length;
            const warns   = allIssues.filter((i) => i.severity === "warning").length;
            if (errors + warns === 0) return null;
            return (
              <div
                style={{
                  fontSize: 11,
                  padding: "6px 8px",
                  marginBottom: 6,
                  borderRadius: 4,
                  background: errors > 0 ? "rgba(255,80,80,0.10)" : "rgba(255,170,85,0.10)",
                  color: errors > 0 ? "var(--danger)" : "var(--accent, #ffaa55)",
                }}
                title="Cabinet-wide validation summary"
              >
                {errors > 0 && <>⚠ {errors} error{errors === 1 ? "" : "s"}</>}
                {errors > 0 && warns > 0 && " · "}
                {warns > 0 && <span style={{ color: "var(--accent, #ffaa55)" }}>{warns} warning{warns === 1 ? "" : "s"}</span>}
              </div>
            );
          })()}
          {parts.map((p) => {
            const hasOverride = !!overrides[p.id];
            const partIssues  = issuesByPart.get(p.id) ?? [];
            const partErrors  = partIssues.filter((i) => i.severity === "error").length;
            const partWarns   = partIssues.length - partErrors;
            return (
              <button
                key={p.id}
                onClick={() => setSelectedPart(p.id)}
                style={{
                  width: "100%", textAlign: "left", padding: "8px 10px",
                  fontSize: 12, margin: 0, marginBottom: 2,
                  border: "1px solid transparent",
                  background: selectedPart === p.id ? "rgba(255,170,85,0.10)" : "transparent",
                  borderColor: selectedPart === p.id ? "var(--accent, #ffaa55)" : "transparent",
                  borderRadius: 4,
                  display: "flex", alignItems: "center", justifyContent: "space-between",
                }}
              >
                <span>{p.name}</span>
                <span style={{ display: "flex", alignItems: "center", gap: 4, fontSize: 10 }}>
                  {partErrors > 0 && <span style={{ color: "var(--danger)" }}>⚠ {partErrors}</span>}
                  {partWarns > 0 && <span style={{ color: "var(--accent, #ffaa55)" }}>{partWarns}</span>}
                  {hasOverride && <span className="muted">●</span>}
                </span>
              </button>
            );
          })}
        </aside>

        <main style={{ overflowY: "auto", padding: 16 }}>
          {selectedPart && (() => {
            const p = parts.find((x) => x.id === selectedPart);
            if (!p) return null;
            return (
              <PartFields
                part={p}
                override={overrides[p.id]}
                stdDrillings={standardPattern[p.id] ?? []}
                autoDrillings={autoDrillingsFor(cabinet, p.id)}
                issues={issuesByPart.get(p.id) ?? []}
                edgeBands={edgeBands}
                onChange={(next) => updatePart(p.id, next)}
              />
            );
          })()}
        </main>
      </div>
    </div>
  );
}

// ── Per-part form ─────────────────────────────────────────────────

interface PartFieldsProps {
  part: CabinetPart;
  override: PartOverride | undefined;
  stdDrillings: DrillEntry[];
  autoDrillings: DrillEntry[];
  issues: Issue[];
  edgeBands: EdgeBand[];
  onChange: (next: PartOverride) => void;
}

function PartFields({ part, override, stdDrillings, autoDrillings, issues, edgeBands, onChange }: PartFieldsProps) {
  const suppressed = new Set(override?.suppressDrillings ?? []);
  const extras = override?.extraDrillings ?? [];
  const extraRowRefs = useRef<Map<number, HTMLTableRowElement | null>>(new Map());
  const [focusedExtra, setFocusedExtra] = useState<number | null>(null);

  // Clear the focus pulse a beat after it lands so it doesn't linger.
  useEffect(() => {
    if (focusedExtra == null) return;
    const t = setTimeout(() => setFocusedExtra(null), 1400);
    return () => clearTimeout(t);
  }, [focusedExtra]);

  function focusExtra(idx: number) {
    setFocusedExtra(idx);
    const row = extraRowRefs.current.get(idx);
    row?.scrollIntoView({ behavior: "smooth", block: "nearest" });
  }

  function setEdge(side: "t" | "b" | "l" | "r", id: Id | null | undefined) {
    const edges = { ...(override?.edges ?? {}) };
    if (id === undefined) delete edges[side];
    else edges[side] = id;
    onChange({ ...(override ?? {}), edges });
  }

  function toggleSuppress(idx: number) {
    const next = new Set(suppressed);
    if (next.has(idx)) next.delete(idx); else next.add(idx);
    onChange({ ...(override ?? {}), suppressDrillings: [...next].sort((a, b) => a - b) });
  }

  function addExtra() {
    const next: CustomDrilling = {
      x: Math.max(0.5, part.w / 2),
      y: Math.max(0.5, part.h / 2),
      diameter: 0.197,   // 5mm
      depth:    0.5,
      face: "inside",
      kind: "cable",
    };
    onChange({ ...(override ?? {}), extraDrillings: [...extras, next] });
  }

  function updateExtra(i: number, patch: Partial<CustomDrilling>) {
    const next = extras.slice();
    next[i] = { ...next[i]!, ...patch };
    onChange({ ...(override ?? {}), extraDrillings: next });
  }

  function removeExtra(i: number) {
    const next = extras.slice();
    next.splice(i, 1);
    onChange({ ...(override ?? {}), extraDrillings: next });
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 18 }}>
      <header>
        <h4 style={{ margin: 0 }}>{part.name}</h4>
        <div className="muted" style={{ fontSize: 11, marginTop: 2 }}>
          {part.w.toFixed(2)}″ × {part.h.toFixed(2)}″ × {part.thickness.toFixed(3)}″
          {part.qty != null && part.qty > 1 && <> · qty {part.qty}</>}
        </div>
      </header>

      <PartPreview
        part={part}
        stdDrillings={stdDrillings}
        autoDrillings={autoDrillings}
        extras={extras}
        suppressedSet={suppressed}
        edgeBands={edgeBands}
        onToggleSuppress={toggleSuppress}
        onFocusExtra={focusExtra}
      />

      {issues.length > 0 && (
        <section
          style={{
            padding: 10,
            borderRadius: 4,
            background: issues.some((i) => i.severity === "error")
              ? "rgba(255,80,80,0.08)"
              : "rgba(255,170,85,0.08)",
            border: `1px solid ${issues.some((i) => i.severity === "error") ? "var(--danger)" : "var(--accent, #ffaa55)"}`,
          }}
        >
          <div style={{ fontSize: 11, fontWeight: 600, marginBottom: 6, textTransform: "uppercase", letterSpacing: 0.4 }}>
            {issues.length} issue{issues.length === 1 ? "" : "s"}
          </div>
          <ul style={{ margin: 0, paddingLeft: 16, fontSize: 11, lineHeight: 1.5 }}>
            {issues.map((iss, i) => (
              <li
                key={i}
                style={{ color: iss.severity === "error" ? "var(--danger)" : "var(--fg)" }}
              >
                <span style={{ fontWeight: 500, marginRight: 4 }}>
                  [{iss.severity}]
                </span>
                {iss.message}
              </li>
            ))}
          </ul>
        </section>
      )}

      {/* ── Part-level parametric overrides ── */}
      <section>
        <h5 style={{ margin: "0 0 8px", fontSize: 12, textTransform: "uppercase", letterSpacing: 0.4, color: "var(--fg-dim)" }}>
          Part overrides
        </h5>
        <div style={{ display: "grid", gridTemplateColumns: "auto 1fr", gap: "6px 12px", alignItems: "center" }}>
          {/* Enabled toggle */}
          <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 11, cursor: "pointer" }}>
            <input
              type="checkbox"
              style={{ width: "auto" }}
              checked={override?.enabled !== false}
              onChange={(e) => {
                const enabled = e.target.checked;
                // `true` is the class default — store undefined so the override
                // collapses cleanly when no per-part state remains.
                onChange({ ...(override ?? {}), enabled: enabled ? undefined : false });
              }}
            />
            Enabled
          </label>
          <span className="muted" style={{ fontSize: 10 }}>
            Uncheck to remove this part from the cabinet entirely.
          </span>

          {/* Thickness override */}
          <span className="label" style={{ fontSize: 10 }}>Thickness ″</span>
          <input
            type="number"
            step={0.0625}
            placeholder={part.thickness.toFixed(3)}
            value={override?.thickness === undefined ? "" : String(override.thickness)}
            onChange={(e) => {
              const raw = e.target.value;
              if (raw === "") {
                onChange({ ...(override ?? {}), thickness: undefined });
                return;
              }
              const v = Number(raw);
              if (Number.isFinite(v)) onChange({ ...(override ?? {}), thickness: v });
            }}
            style={{ width: 100, fontSize: 12 }}
          />
        </div>
      </section>

      {/* ── Edges ── */}
      <section>
        <h5 style={{ margin: "0 0 8px", fontSize: 12, textTransform: "uppercase", letterSpacing: 0.4, color: "var(--fg-dim)" }}>Edge banding</h5>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
          <EdgeField label="Top"    value={part.edges.t} edgeBands={edgeBands} onChange={(v) => setEdge("t", v)} />
          <EdgeField label="Bottom" value={part.edges.b} edgeBands={edgeBands} onChange={(v) => setEdge("b", v)} />
          <EdgeField label="Left"   value={part.edges.l} edgeBands={edgeBands} onChange={(v) => setEdge("l", v)} />
          <EdgeField label="Right"  value={part.edges.r} edgeBands={edgeBands} onChange={(v) => setEdge("r", v)} />
        </div>
      </section>

      {/* ── Standard drillings ── */}
      {stdDrillings.length > 0 && (
        <section>
          <h5 style={{ margin: "0 0 8px", fontSize: 12, textTransform: "uppercase", letterSpacing: 0.4, color: "var(--fg-dim)" }}>
            Standard drillings ({stdDrillings.length - suppressed.size} active / {stdDrillings.length} total)
          </h5>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(180px, 1fr))", gap: 4, maxHeight: 200, overflowY: "auto", padding: 4, border: "1px solid var(--line)", borderRadius: 4 }}>
            {stdDrillings.map((d, i) => {
              const off = suppressed.has(i);
              return (
                <label key={i} style={{
                  display: "flex", alignItems: "center", gap: 6, padding: "2px 6px",
                  fontSize: 11, cursor: "pointer",
                  opacity: off ? 0.45 : 1,
                  textDecoration: off ? "line-through" : "none",
                }}>
                  <input
                    type="checkbox"
                    style={{ width: "auto" }}
                    checked={!off}
                    onChange={() => toggleSuppress(i)}
                  />
                  <span className="muted" style={{ fontSize: 10 }}>{d.kind}</span>
                  <span>{toMm(d.x)}×{toMm(d.y)}mm</span>
                  <span className="muted" style={{ fontSize: 10 }}>⌀{toMm(d.diameter)}</span>
                </label>
              );
            })}
          </div>
        </section>
      )}

      {/* ── Custom drillings ── */}
      <section>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8 }}>
          <h5 style={{ margin: 0, fontSize: 12, textTransform: "uppercase", letterSpacing: 0.4, color: "var(--fg-dim)" }}>
            Custom drillings ({extras.length})
          </h5>
          <button className="ghost" style={{ fontSize: 11, padding: "2px 8px" }} onClick={addExtra}>+ Add hole</button>
        </div>
        {extras.length === 0 ? (
          <div className="muted" style={{ fontSize: 11 }}>No custom holes yet.</div>
        ) : (
          <table style={{ width: "100%", fontSize: 11, borderCollapse: "collapse" }}>
            <thead>
              <tr style={{ color: "var(--fg-dim)" }}>
                <th style={th}>X″</th>
                <th style={th}>Y″</th>
                <th style={th}>⌀″</th>
                <th style={th}>Depth″</th>
                <th style={th}>Face</th>
                <th style={th}>Kind</th>
                <th style={th}></th>
              </tr>
            </thead>
            <tbody>
              {extras.map((d, i) => (
                <tr
                  key={i}
                  ref={(el) => { extraRowRefs.current.set(i, el); }}
                  style={{
                    background: focusedExtra === i ? "rgba(255,170,85,0.18)" : undefined,
                    transition: "background 400ms ease-out",
                  }}
                >
                  <td style={td}><NumCell value={d.x}        step={0.125} onChange={(v) => updateExtra(i, { x: v })} /></td>
                  <td style={td}><NumCell value={d.y}        step={0.125} onChange={(v) => updateExtra(i, { y: v })} /></td>
                  <td style={td}><NumCell value={d.diameter} step={0.05}  onChange={(v) => updateExtra(i, { diameter: v })} /></td>
                  <td style={td}><NumCell value={d.depth}    step={0.05}  onChange={(v) => updateExtra(i, { depth: v })} /></td>
                  <td style={td}>
                    <select value={d.face} onChange={(e) => updateExtra(i, { face: e.target.value as CustomDrilling["face"] })} style={cellInput}>
                      <option value="inside">inside</option>
                      <option value="back">back</option>
                      <option value="front">front</option>
                    </select>
                  </td>
                  <td style={td}>
                    <select value={d.kind} onChange={(e) => updateExtra(i, { kind: e.target.value as CustomDrilling["kind"] })} style={cellInput}>
                      <option value="shelf_pin">shelf_pin</option>
                      <option value="hinge_cup">hinge_cup</option>
                      <option value="screw">screw</option>
                      <option value="cable">cable</option>
                      <option value="custom">custom</option>
                    </select>
                  </td>
                  <td style={td}>
                    <button className="ghost" onClick={() => removeExtra(i)} style={{ padding: "0 6px", fontSize: 11, color: "var(--danger)" }}>×</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>

      {/* ── Reset ── */}
      {(override?.edges
        || override?.suppressDrillings?.length
        || override?.extraDrillings?.length
        || override?.materialId != null
        || override?.enabled !== undefined
        || override?.thickness !== undefined
      ) && (
        <div>
          <button
            className="ghost"
            style={{ fontSize: 11, padding: "2px 10px", color: "var(--danger)" }}
            onClick={() => onChange({})}
          >Reset all overrides on this part</button>
        </div>
      )}
    </div>
  );
}

// ── Subcomponents ─────────────────────────────────────────────────

const th: React.CSSProperties = { textAlign: "left", padding: "4px 6px", fontWeight: 500, borderBottom: "1px solid var(--line)" };
const td: React.CSSProperties = { padding: "2px 6px" };
const cellInput: React.CSSProperties = { width: "100%", fontSize: 11, padding: "2px 4px" };

function EdgeField({
  label, value, edgeBands, onChange,
}: {
  label: string;
  value: Id | null;
  edgeBands: EdgeBand[];
  onChange: (v: Id | null) => void;
}) {
  return (
    <label style={{ display: "block" }}>
      <span className="label" style={{ fontSize: 10 }}>{label}</span>
      <select
        value={value == null ? "" : String(value)}
        onChange={(e) => onChange(e.target.value === "" ? null : Number(e.target.value))}
        style={{ width: "100%", fontSize: 12, marginTop: 2 }}
      >
        <option value="">— none —</option>
        {edgeBands.map((eb) => (
          <option key={eb._id} value={eb._id}>{eb.name}</option>
        ))}
      </select>
    </label>
  );
}

function NumCell({ value, step, onChange }: { value: number; step: number; onChange: (v: number) => void }) {
  return (
    <input
      type="number"
      value={value}
      step={step}
      onChange={(e) => {
        const v = Number(e.target.value);
        if (Number.isFinite(v)) onChange(v);
      }}
      style={cellInput}
    />
  );
}

function toMm(inches: number): string {
  return (inches * 25.4).toFixed(1);
}
